if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      connectedCallback() {
        this.elements = {
          viewer: this.querySelector('[data-gallery-main]'), thumbnails: this.querySelector('[data-gallery-thumbnails]'),
          dots: this.querySelector('[data-gallery-dots]'), previous: this.querySelector('[name="previous"]'),
          next: this.querySelector('[name="next"]'), zoom: this.querySelector('[data-gallery-zoom]'),
          counter: this.querySelector('[data-gallery-counter]'),
        };
        this.onControlClick = this.handleControlClick.bind(this);
        this.onPreviousClick = () => this.showAdjacentMedia(-1);
        this.onNextClick = () => this.showAdjacentMedia(1);
        this.onZoomClick = this.openZoom.bind(this);
        this.elements.thumbnails?.addEventListener('click', this.onControlClick);
        this.elements.dots?.addEventListener('click', this.onControlClick);
        this.elements.previous?.addEventListener('click', this.onPreviousClick);
        this.elements.next?.addEventListener('click', this.onNextClick);
        this.elements.zoom?.addEventListener('click', this.onZoomClick);
        this.createSlider();
      }

      disconnectedCallback() {
        this.elements.thumbnails?.removeEventListener('click', this.onControlClick);
        this.elements.dots?.removeEventListener('click', this.onControlClick);
        this.elements.previous?.removeEventListener('click', this.onPreviousClick);
        this.elements.next?.removeEventListener('click', this.onNextClick);
        this.elements.zoom?.removeEventListener('click', this.onZoomClick);
        this.slider?.destroy();
        this.slider = null;
      }

      createSlider() {
        if (!this.elements.viewer) return;
        this.setActiveMedia(this.elements.viewer.querySelector('[data-selected-variant-featured-media]')?.dataset.mediaId);
        if (!window.KeenSlider) return;
        this.sliderOptions = {
          selector: '.product__media-item:not([hidden])',
          initial: 0,
          slides: { perView: 1 },
          slideChanged: () => this.syncActive(this.eligibleSlides()[this.slider.track.details.rel]),
          updated: () => this.syncControls(),
        };
        try {
          this.slider = new window.KeenSlider(this.elements.viewer, this.sliderOptions);
        } catch (error) {
          this.slider = null;
          return;
        }
        this.allSlides().forEach((slide) => slide.classList.remove('gallery-fallback-hidden'));
        this.syncActive(this.eligibleSlides()[0]);
      }

      handleControlClick(event) {
        const button = event.target.closest('button[data-media-id]');
        if (!button || button.hidden) return;
        this.showMedia(button.dataset.mediaId);
      }

      allSlides() { return Array.from(this.elements.viewer?.querySelectorAll('.product__media-item') || []); }
      eligibleSlides() { return this.allSlides().filter((slide) => !slide.hidden); }
      activeSlide() { return this.allSlides().find((slide) => slide.classList.contains('is-active')); }

      showMedia(mediaId) {
        const index = this.eligibleSlides().findIndex((slide) => slide.dataset.mediaId === mediaId);
        if (index < 0) return;
        if (this.slider) this.slider.moveToIdx(index);
        else this.syncActive(this.eligibleSlides()[index]);
      }

      showAdjacentMedia(direction) {
        const slides = this.eligibleSlides();
        const nextIndex = slides.indexOf(this.activeSlide()) + direction;
        if (nextIndex >= 0 && nextIndex < slides.length) this.showMedia(slides[nextIndex].dataset.mediaId);
      }

      syncActive(activeItem) {
        if (!activeItem) { this.syncControls(); return; }
        this.allSlides().forEach((item) => item.classList.toggle('is-active', item === activeItem));
        if (!this.slider) this.allSlides().forEach((item) => item.classList.toggle('gallery-fallback-hidden', item !== activeItem));
        [this.elements.thumbnails, this.elements.dots].forEach((container) => {
          container?.querySelectorAll('button[data-media-id]').forEach((button) => {
            const active = button.dataset.mediaId === activeItem.dataset.mediaId;
            button.classList.toggle('is-active', active);
            button.classList.toggle('slider-dot--current', active);
            button.setAttribute('aria-current', active ? 'true' : 'false');
            button.parentElement?.classList.toggle('is-active', active);
          });
        });
        if (this.elements.zoom) this.elements.zoom.hidden = activeItem.dataset.mediaType !== 'image';
        this.scrollActiveThumbnailIntoView(activeItem.dataset.mediaId);
        this.preventStickyHeader();
        this.playActiveMedia(activeItem);
        this.syncControls();
      }

      syncControls() {
        const slides = this.eligibleSlides();
        const index = Math.max(slides.indexOf(this.activeSlide()), 0);
        const hasMultipleSlides = slides.length > 1;
        if (this.elements.previous) { this.elements.previous.hidden = !hasMultipleSlides; this.elements.previous.disabled = index === 0; }
        if (this.elements.next) { this.elements.next.hidden = !hasMultipleSlides; this.elements.next.disabled = index === slides.length - 1; }
        if (this.elements.counter) {
          this.elements.counter.querySelector('.slider-counter--current').textContent = index + 1;
          this.elements.counter.querySelector('.slider-counter--total').textContent = slides.length;
        }
        [this.elements.thumbnails, this.elements.dots].forEach((container) => {
          if (!container) return;
          container.hidden = !hasMultipleSlides;
          container.querySelectorAll('button[data-media-id]').forEach((button) => {
            const eligible = slides.some((slide) => slide.dataset.mediaId === button.dataset.mediaId);
            const thumbnail = button.closest('.product-gallery__thumbnail');
            if (thumbnail) thumbnail.hidden = !eligible;
            else button.hidden = !eligible;
          });
        });
      }

      scrollActiveThumbnailIntoView(mediaId) {
        const container = this.elements.thumbnails;
        const thumbnail = container?.querySelector(`button[data-media-id="${mediaId}"]`);
        if (!container || !thumbnail) return;
        const containerRect = container.getBoundingClientRect();
        const thumbnailRect = thumbnail.getBoundingClientRect();
        if (thumbnailRect.left < containerRect.left) container.scrollLeft -= containerRect.left - thumbnailRect.left;
        if (thumbnailRect.right > containerRect.right) container.scrollLeft += thumbnailRect.right - containerRect.right;
        if (thumbnailRect.top < containerRect.top) container.scrollTop -= containerRect.top - thumbnailRect.top;
        if (thumbnailRect.bottom > containerRect.bottom) container.scrollTop += thumbnailRect.bottom - containerRect.bottom;
      }

      openZoom() {
        const activeItem = this.activeSlide();
        const modal = document.querySelector(`#ProductModal-${this.id.replace('MediaGallery-', '')}`);
        if (!activeItem || activeItem.dataset.mediaType !== 'image' || !modal?.show || !this.elements.zoom) return;
        this.elements.zoom.dataset.mediaId = activeItem.dataset.mediaId.split('-').pop();
        modal.show(this.elements.zoom);
      }

      setActiveMedia(mediaId) {
        const slides = this.allSlides();
        const selectedMedia = slides.find((slide) => slide.dataset.mediaId === mediaId);
        const fallbackMedia = slides.find((slide) => slide.dataset.variantMedia !== 'true') || slides[0];
        const nextMedia = selectedMedia || fallbackMedia;
        slides.forEach((slide) => { slide.hidden = slide.dataset.variantMedia === 'true' && slide !== nextMedia; slide.classList.remove('gallery-fallback-hidden'); });
        const targetIndex = this.eligibleSlides().indexOf(nextMedia);
        if (this.slider && targetIndex >= 0) {
          this.slider.update(this.sliderOptions, targetIndex);
          this.syncActive(this.eligibleSlides()[this.slider.track.details.rel] || nextMedia);
        } else {
          this.syncActive(nextMedia);
        }
      }

      playActiveMedia(activeItem) { window.pauseAllMedia?.(); activeItem.querySelector('.deferred-media')?.loadContent(false); }
      preventStickyHeader() { (this.stickyHeader ||= document.querySelector('sticky-header'))?.dispatchEvent(new Event('preventHeaderReveal')); }
    }
  );
}
